(() => {
  const isAndroid = () => {
    const platform = navigator.userAgentData?.platform || '';
    return platform.toLowerCase() === 'android' || /Android/i.test(navigator.userAgent || '');
  };

  const init = (section) => {
    if (!section || section.dataset.dsVideosReady === 'true') return;
    section.dataset.dsVideosReady = 'true';

    const rail = section.querySelector('[data-ds-video-rail]');
    const items = [...section.querySelectorAll('[data-ds-video-item]')];
    const previous = section.querySelector('[data-ds-video-prev]');
    const next = section.querySelector('[data-ds-video-next]');
    const android = isAndroid();
    let sectionVisible = false;
    const visibleItems = new Map();
    if (!rail || !items.length) return;

    const hostFor = (item) => item.querySelector('[data-ds-video-host]');
    const toggleFor = (item) => item.querySelector('[data-ds-video-toggle]');
    const labelFor = (item) => hostFor(item)?.dataset.mediaLabel || 'DeepSweep product demonstration';

    const setPlayingState = (item, playing) => {
      item.classList.toggle('is-playing', playing);
      const toggle = toggleFor(item);
      if (toggle) toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${labelFor(item)}`);
    };

    const createMedia = (item) => {
      const host = hostFor(item);
      if (!host || host.firstElementChild) return host?.firstElementChild;

      if (android) {
        const image = document.createElement('img');
        image.className = 'ds-video-products__animation';
        image.alt = host.dataset.mediaLabel || 'DeepSweep product demonstration';
        image.decoding = 'async';
        image.draggable = false;
        image.dataset.animationSrc = host.dataset.animationSrc || '';
        image.src = host.dataset.posterSrc || '';
        image.addEventListener('load', () => {
          if (image.getAttribute('src') === image.dataset.animationSrc) item.classList.add('is-playing');
        });
        host.appendChild(image);
        return image;
      }

      const video = document.createElement('video');
      video.className = 'ds-video-products__video';
      video.preload = 'none';
      video.poster = host.dataset.posterSrc || '';
      video.setAttribute('aria-label', host.dataset.mediaLabel || 'DeepSweep product demonstration');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('disableremoteplayback', '');
      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.addEventListener('play', () => setPlayingState(item, true));
      video.addEventListener('pause', () => setPlayingState(item, false));
      try {
        video.disablePictureInPicture = true;
        video.disableRemotePlayback = true;
      } catch (_error) {}
      host.appendChild(video);
      return video;
    };

    const loadVideo = (item) => {
      const video = createMedia(item);
      const host = hostFor(item);
      if (!(video instanceof HTMLVideoElement) || video.dataset.loaded === 'true') return video;
      const source = document.createElement('source');
      source.src = host?.dataset.videoSrc || '';
      source.type = 'video/mp4';
      video.appendChild(source);
      video.dataset.loaded = 'true';
      video.load();
      return video;
    };

    const play = (item, force = false) => {
      if (!sectionVisible || visibleItems.get(item) !== true) return;
      if (!force && item.dataset.dsUserPaused === 'true') return;
      const media = createMedia(item);
      if (android) {
        if (media?.dataset.animationSrc && media.getAttribute('src') !== media.dataset.animationSrc) media.src = media.dataset.animationSrc;
        setPlayingState(item, true);
        return;
      }
      const video = loadVideo(item);
      if (!(video instanceof HTMLVideoElement)) return;
      const promise = video.play();
      promise?.then(() => setPlayingState(item, true)).catch(() => setPlayingState(item, false));
    };

    const pause = (item, userInitiated = false) => {
      if (userInitiated) item.dataset.dsUserPaused = 'true';
      setPlayingState(item, false);
      const media = createMedia(item);
      if (android) {
        const host = hostFor(item);
        if (media && host?.dataset.posterSrc && media.getAttribute('src') !== host.dataset.posterSrc) media.src = host.dataset.posterSrc;
        return;
      }
      if (media instanceof HTMLVideoElement && !media.paused) media.pause();
    };

    const updateNavigation = () => {
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const overflow = maximum > 3;
      previous?.classList.toggle('is-hidden', !overflow || rail.scrollLeft <= 3);
      next?.classList.toggle('is-hidden', !overflow || rail.scrollLeft >= maximum - 3);
    };

    const scroll = (direction) => rail.scrollBy({ left: direction * Math.max(rail.clientWidth * .82, 1), behavior: 'smooth' });
    previous?.addEventListener('click', () => scroll(-1));
    next?.addEventListener('click', () => scroll(1));
    rail.addEventListener('scroll', updateNavigation, { passive: true });
    window.addEventListener('resize', updateNavigation, { passive: true });

    items.forEach((item) => {
      visibleItems.set(item, false);
      createMedia(item);
      const toggle = toggleFor(item);
      toggle?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (item.classList.contains('is-playing')) {
          pause(item, true);
          return;
        }
        item.dataset.dsUserPaused = 'false';
        sectionVisible = true;
        visibleItems.set(item, true);
        play(item, true);
      });
      item.addEventListener('click', (event) => {
        if (event.target.closest('a, button')) return;
        item.dataset.dsUserPaused = 'false';
        sectionVisible = true;
        visibleItems.set(item, true);
        play(item, true);
      });
    });

    if ('IntersectionObserver' in window) {
      const sectionObserver = new IntersectionObserver(([entry]) => {
        sectionVisible = entry.isIntersecting;
        items.forEach((item) => sectionVisible && visibleItems.get(item) === true ? play(item) : pause(item));
      }, { rootMargin: '80px 0px', threshold: .08 });
      sectionObserver.observe(section);

      const itemObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          visibleItems.set(entry.target, entry.isIntersecting);
          if (entry.isIntersecting && sectionVisible) play(entry.target);
          else pause(entry.target);
        });
      }, { root: rail, threshold: .35 });
      items.forEach((item) => itemObserver.observe(item));
    } else {
      sectionVisible = true;
      items.forEach((item) => {
        visibleItems.set(item, true);
        play(item);
      });
    }

    updateNavigation();
  };

  document.querySelectorAll('[data-ds-video-products]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-ds-video-products]').forEach(init);
  });
})();
